import { createTheme } from '@mui/material/styles';

/* Cherry-Pick "Orchard" tokens — single source of truth.
   Kept in JS (not only CSS vars) so charts/PDF panels can import them. */
export const orchard = {
  ink: '#241E2B',
  paper: '#FBF8F5',
  cherry: '#C2233E',
  cherryDeep: '#8C1230',
  leaf: '#2E7D5B',
  honey: '#E8A93D',
  blossom: '#F7E3E7',
  night: '#161020',
  nightPanel: '#211934',
  cherryBright: '#F25972',
  leafBright: '#4CC38A',
  fontDisplay: "'Unbounded', 'Golos Text', sans-serif",
  fontBody: "'Golos Text', 'Inter', system-ui, sans-serif",
  fontMono: "'JetBrains Mono', 'Consolas', monospace",
  // Neutral for "not assessed yet" — a muted plum, not a grey
  plum: '#9A8FA6',
  plumBright: '#8F84A6',
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

/* Mastery tone shared by dashboards and profile views:
   leaf = strong, honey = developing, cherry = needs work. */
export function masteryTone(mastery, assessed = true, dark = false) {
  if (!assessed) return dark ? orchard.plumBright : orchard.plum;
  if (mastery >= 70) return dark ? orchard.leafBright : orchard.leaf;
  if (mastery >= 40) return orchard.honey;
  return dark ? orchard.cherryBright : orchard.cherry;
}

/* Numeric tints (primary.50 … primary.700) are referenced across the app via
   sx strings. MUI does not generate them, so define them per mode: solid
   tints on paper in light mode, translucent washes on night in dark mode. */
const tints = (hex, rgb, dark, deep) =>
  dark
    ? {
        50: `rgba(${rgb},0.08)`,
        100: `rgba(${rgb},0.14)`,
        200: `rgba(${rgb},0.26)`,
        300: `rgba(${rgb},0.4)`,
        400: `rgba(${rgb},0.65)`,
        700: hex,
      }
    : {
        50: `rgba(${rgb},0.06)`,
        100: `rgba(${rgb},0.12)`,
        200: `rgba(${rgb},0.24)`,
        300: `rgba(${rgb},0.4)`,
        400: `rgba(${rgb},0.7)`,
        700: deep,
      };

export const createAppTheme = (mode) => {
  const dark = mode === 'dark';
  const cherry = dark ? orchard.cherryBright : orchard.cherry;
  const leaf = dark ? orchard.leafBright : orchard.leaf;
  const cherryRgb = dark ? '242,89,114' : '194,35,62';
  const leafRgb = dark ? '76,195,138' : '46,125,91';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: cherry,
        light: dark ? '#FF8095' : '#E05570',
        dark: orchard.cherryDeep,
        contrastText: '#FFFFFF',
        ...tints(cherry, cherryRgb, dark, orchard.cherryDeep),
      },
      secondary: {
        main: leaf,
        light: dark ? '#7FDCB0' : '#57A583',
        dark: '#1D5940',
        contrastText: '#FFFFFF',
        ...tints(leaf, leafRgb, dark, '#1D5940'),
      },
      // Explicit opaque light/dark: without them MUI takes shade 300 (a
      // translucent tint) as `.light`, and Alerts render faded text.
      warning: {
        main: orchard.honey,
        ...tints(orchard.honey, '232,169,61', dark, '#9A6A12'),
        light: dark ? '#F4CB7E' : '#F0C36A',
        dark: '#9A6A12',
      },
      success: {
        main: leaf,
        ...tints(leaf, leafRgb, dark, '#1D5940'),
        light: dark ? '#7FDCB0' : '#57A583',
        dark: '#1D5940',
      },
      // MUI's default error reds, plus the tints
      error: {
        main: dark ? '#f44336' : '#d32f2f',
        ...tints(
          dark ? '#f44336' : '#d32f2f',
          dark ? '244,67,54' : '211,47,47',
          dark,
          '#9A1F1F'
        ),
        light: dark ? '#e57373' : '#ef5350',
        dark: dark ? '#d32f2f' : '#c62828',
      },
      background: {
        default: dark ? orchard.night : orchard.paper,
        paper: dark ? orchard.nightPanel : '#FFFFFF',
      },
      text: {
        primary: dark ? '#F4EEF6' : orchard.ink,
        secondary: dark ? '#B4A8C4' : '#6B6273',
      },
      divider: dark ? 'rgba(244,238,246,0.09)' : 'rgba(36,30,43,0.1)',
    },
    typography: {
      fontFamily: orchard.fontBody,
      h1: { fontFamily: orchard.fontDisplay, fontWeight: 700 },
      h2: { fontFamily: orchard.fontDisplay, fontWeight: 700 },
      h3: {
        fontFamily: orchard.fontDisplay,
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h4: {
        fontFamily: orchard.fontDisplay,
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      h5: { fontWeight: 800, letterSpacing: '-0.01em' },
      h6: { fontWeight: 700 },
      subtitle2: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 700 },
      caption: { letterSpacing: '0.01em' },
      overline: {
        fontFamily: orchard.fontMono,
        fontWeight: 500,
        letterSpacing: '0.14em',
      },
    },
    shape: { borderRadius: 14 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: dark ? orchard.night : orchard.paper,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            padding: '9px 22px',
            boxShadow: 'none',
            transition:
              'transform 0.18s cubic-bezier(0.22,1,0.36,1), box-shadow 0.18s ease, background 0.18s ease',
            '&:hover': {
              boxShadow: `0 10px 22px -8px ${dark ? 'rgba(242,89,114,0.5)' : 'rgba(140,18,48,0.35)'}`,
              transform: 'translateY(-1px)',
            },
            '&:active': { transform: 'translateY(0) scale(0.98)' },
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${cherry} 0%, ${orchard.cherryDeep} 130%)`,
            // without this the gradient stayed under the grey disabled label
            '&.Mui-disabled': {
              background: dark
                ? 'rgba(244,238,246,0.12)'
                : 'rgba(36,30,43,0.12)',
            },
          },
          outlined: {
            borderWidth: 1.5,
            '&:hover': { borderWidth: 1.5 },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'transform 0.18s ease, background 0.18s ease',
            '&:hover': { transform: 'translateY(-1px)' },
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: '1px solid',
            borderColor: dark
              ? 'rgba(244,238,246,0.09)'
              : 'rgba(36,30,43,0.08)',
            backgroundImage: 'none',
            transition:
              'transform 0.25s cubic-bezier(0.22,1,0.36,1), box-shadow 0.25s ease, border-color 0.25s ease',
            '&:hover': {
              transform: 'translateY(-3px)',
              borderColor: dark
                ? 'rgba(242,89,114,0.45)'
                : 'rgba(194,35,62,0.3)',
              boxShadow: dark
                ? '0 18px 40px -18px rgba(0,0,0,0.6)'
                : '0 18px 40px -18px rgba(140,18,48,0.22)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            boxShadow: dark
              ? '0 1px 3px rgba(0,0,0,0.5)'
              : '0 1px 3px rgba(36,30,43,0.07), 0 1px 2px rgba(36,30,43,0.05)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 8 },
          colorPrimary: {
            backgroundColor: dark ? 'rgba(242,89,114,0.18)' : orchard.blossom,
            color: dark ? orchard.cherryBright : orchard.cherryDeep,
          },
          // MUI darkens clickable primary chips to primary.dark on hover/focus,
          // which hid the dark label (and hover sticks after a tap on phones)
          clickableColorPrimary: {
            '&:hover, &.Mui-focusVisible': {
              backgroundColor: dark ? 'rgba(242,89,114,0.28)' : '#F2CDD5',
            },
          },
          deleteIconColorPrimary: {
            color: dark ? orchard.cherryBright : orchard.cherryDeep,
            opacity: 0.75,
            '&:hover': {
              color: dark ? orchard.cherryBright : orchard.cherryDeep,
              opacity: 1,
            },
          },
          deletableColorPrimary: {
            '&.Mui-focusVisible': {
              backgroundColor: dark ? 'rgba(242,89,114,0.28)' : '#F2CDD5',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            transition: 'box-shadow 0.18s ease',
            '&.Mui-focused': {
              boxShadow: `0 0 0 4px ${dark ? 'rgba(242,89,114,0.18)' : 'rgba(194,35,62,0.12)'}`,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 20 },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: dark ? '#3A2E52' : orchard.ink,
            borderRadius: 8,
            fontWeight: 500,
            fontSize: '0.75rem',
            padding: '6px 10px',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 99,
            height: 8,
            backgroundColor: dark
              ? 'rgba(244,238,246,0.08)'
              : 'rgba(36,30,43,0.07)',
          },
          bar: { borderRadius: 99 },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: 3,
            background: `linear-gradient(90deg, ${cherry}, ${orchard.honey})`,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            transition: 'background 0.18s ease, color 0.18s ease',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: { fontWeight: 700 },
        },
      },
      // One visible keyboard focus ring for every clickable surface
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.Mui-focusVisible': {
              outline: `2px solid ${cherry}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiSkeleton: {
        defaultProps: { animation: 'wave' },
        styleOverrides: {
          root: {
            backgroundColor: dark
              ? 'rgba(244,238,246,0.07)'
              : 'rgba(36,30,43,0.06)',
          },
        },
      },
    },
  });
};

export default createAppTheme('light');
